import { ACTION_TYPES, TRIGGER_SUBTYPES, } from "./agent-workflow.js";
/**
 * Workflow capability catalogue + structural graph validator.
 *
 * This file is the single source of truth for "what a workflow can contain".
 * It is consumed by:
 *   - the UI config forms (what fields to render per node),
 *   - the chat-driven authoring skill (so an agent knows what's possible
 *     before it emits a graph — see buildWorkflowCapabilityCatalogue),
 *   - server-side validation at run-start and at authoring time
 *     (validateWorkflowGraph below + the semantic checks in
 *     server/services/workflow-validate.ts).
 *
 * Keep it framework-free (no zod-runtime, no DB) so both the browser bundle
 * and the agent-facing catalogue endpoint can import it cheaply.
 */
// --------------------------------------------------------------------
// Condition operators (must mirror server/services/workflow-jsonlogic.ts)
// --------------------------------------------------------------------
export const WORKFLOW_CONDITION_OPERATORS = [
    "==",
    "!=",
    ">",
    ">=",
    "<",
    "<=",
    "and",
    "or",
    "!",
    "var",
];
/** Whole-run safety guard for loops and unexpectedly long graphs. */
export const MAX_WORKFLOW_STEP_EXECUTIONS = 100;
// --------------------------------------------------------------------
// Trigger catalogue
// --------------------------------------------------------------------
export const WORKFLOW_TRIGGER_CATALOGUE = [
    {
        subtype: "manual",
        label: "Manual",
        description: "Started by a person clicking Run (or via the start-run API). No schedule.",
        configFields: [],
        implemented: true,
    },
    {
        subtype: "schedule",
        label: "Schedule (cron)",
        description: "Fires on a cron schedule. The platform owns the timer (node-cron); the assigned agent is woken on each tick. Independent of the agent runtime's own cron support.",
        configFields: [
            {
                key: "cronExpression",
                label: "Cron expression",
                type: "cron",
                required: true,
                description: "Standard 5- or 6-field cron, e.g. '0 9 * * 1' for 09:00 every Monday.",
            },
            {
                key: "timezone",
                label: "Timezone",
                type: "string",
                required: false,
                description: "IANA tz name, e.g. 'Europe/London'. Defaults to UTC.",
            },
        ],
        implemented: true,
    },
    {
        subtype: "webhook",
        label: "Inbound webhook",
        description: "Fires when an external system POSTs to the workflow's webhook URL with the shared secret. The POST body becomes the run inputs.",
        configFields: [
            {
                key: "webhookId",
                label: "Webhook id",
                type: "string",
                required: false,
                description: "Assigned by the platform when the workflow is activated. Read-only.",
            },
        ],
        implemented: true,
    },
    {
        subtype: "channel_message",
        label: "Channel message",
        description: "Fires when a message is posted to a watched agent channel. The message text becomes the run input `message`.",
        configFields: [
            {
                key: "channelId",
                label: "Channel",
                type: "uuid",
                required: true,
                description: "The agent channel to watch for new messages.",
            },
        ],
        implemented: true,
    },
];
// --------------------------------------------------------------------
// Action catalogue
// --------------------------------------------------------------------
export const WORKFLOW_ACTION_CATALOGUE = [
    {
        actionType: "mcp_tool",
        label: "MCP tool call",
        description: "Invoke any MCP tool the executing agent already has access to. The primary, most flexible action shape.",
        configFields: [
            {
                key: "tool",
                label: "Tool name",
                type: "string",
                required: true,
                description: "Fully-qualified MCP tool name the executing agent can call, e.g. 'proclaw-agent-comms.teams_send_message'.",
            },
            {
                key: "args",
                label: "Arguments (JSON)",
                type: "json",
                required: false,
                description: "JSON object of arguments for the tool. May reference prior step outputs via {{steps.<id>.outputs.<key>}}.",
            },
        ],
        implemented: true,
    },
    {
        actionType: "http_post",
        label: "HTTP POST",
        description: "POST a JSON body to an external URL.",
        configFields: [
            {
                key: "url",
                label: "URL",
                type: "string",
                required: true,
                description: "Absolute https URL to POST to.",
            },
            {
                key: "body",
                label: "Body (JSON)",
                type: "json",
                required: false,
                description: "JSON body to send.",
            },
        ],
        implemented: true,
    },
    {
        actionType: "send_email",
        label: "Send email",
        description: "Send an email via the executing agent's mail tool. (Convenience wrapper over an MCP tool.)",
        configFields: [
            { key: "to", label: "To", type: "string", required: true, description: "Recipient address." },
            { key: "cc", label: "CC", type: "string", required: false, description: "Optional CC recipients, separated by commas or semicolons." },
            { key: "subject", label: "Subject", type: "string", required: true, description: "Email subject." },
            { key: "body", label: "Body", type: "text", required: true, description: "Email body (markdown ok)." },
        ],
        implemented: true,
    },
    {
        actionType: "wait_for_email_reply",
        label: "Wait for email reply",
        description: "Park the workflow until the assigned agent's mailbox receives a reply or the fixed deadline passes, then continue through its outgoing edge.",
        configFields: [
            { key: "messageId", label: "Message id", type: "string", required: false, description: "Immutable Graph message id returned by the send step." },
            { key: "conversationId", label: "Conversation id", type: "string", required: false, description: "Graph conversation id returned by the send step." },
            { key: "internetMessageId", label: "Internet message id", type: "string", required: false, description: "Message-ID returned by the send step." },
            { key: "expectedFrom", label: "Expected sender", type: "string", required: true, description: "Sender address required before a reply can be treated as actionable." },
            { key: "sentAt", label: "Sent at", type: "string", required: true, description: "Original send timestamp in ISO 8601 format." },
            { key: "waitDuration", label: "Wait duration", type: "number", required: false, description: "Positive whole-number duration. Required with waitUnit for new workflows." },
            { key: "waitUnit", label: "Wait unit", type: "string", required: false, description: "minutes, hours, or days. Required with waitDuration for new workflows." },
            { key: "ignoredMessageIds", label: "Ignored message ids", type: "json", required: false, description: "Previously handled reply ids, such as an out-of-office response." },
        ],
        implemented: true,
    },
    {
        actionType: "post_to_channel",
        label: "Post to channel",
        description: "Post a message into an agent channel.",
        configFields: [
            { key: "channelId", label: "Channel id", type: "uuid", required: true, description: "Target agent channel." },
            { key: "message", label: "Message", type: "text", required: true, description: "Message text (markdown ok)." },
        ],
        implemented: true,
    },
];
// --------------------------------------------------------------------
// Node catalogue
// --------------------------------------------------------------------
export const WORKFLOW_NODE_CATALOGUE = [
    {
        type: "trigger",
        label: "Trigger",
        description: "Entry point. Exactly one per workflow. Its subtype decides how the run starts.",
        configFields: [
            { key: "label", label: "Label", type: "string", required: false, description: "Display name." },
            { key: "subtype", label: "Trigger type", type: "string", required: true, description: `One of: ${TRIGGER_SUBTYPES.join(", ")}.` },
        ],
        outgoing: "single",
    },
    {
        type: "agent",
        label: "Agent step",
        description: "A prompt for the assigned agent to execute, producing structured output the next steps can read.",
        configFields: [
            { key: "label", label: "Label", type: "string", required: false, description: "Display name." },
            { key: "agentId", label: "Agent", type: "uuid", required: true, description: "The agent that executes this step." },
            { key: "promptTemplate", label: "Prompt template", type: "text", required: true, description: "Instructions for the agent. May reference {{input}} and {{steps.<id>.outputs.<key>}}." },
            { key: "model", label: "Model", type: "string", required: false, description: "Optional model override for this step — a modelId from the catalogue (see the `models` list returned by get_workflow_capabilities). Omit to run on the agent's own configured model." },
            { key: "fallbackModel", label: "Backup model", type: "string", required: false, description: "Optional backup model used when the primary model fails or the step is retried. The agent's own configured model is always the final fallback." },
        ],
        outgoing: "single",
    },
    {
        type: "condition",
        label: "Condition",
        description: "Evaluated server-side over prior step outputs. Supports yes/no or ordered multiple-choice branches.",
        configFields: [
            { key: "label", label: "Label", type: "string", required: false, description: "Display name." },
            { key: "mode", label: "Branching mode", type: "string", required: false, description: "Either binary (default) or multiple." },
            { key: "expression", label: "Expression", type: "json", required: false, description: `Binary-mode JSONLogic over {inputs, steps}. Operators: ${WORKFLOW_CONDITION_OPERATORS.join(" ")}.` },
            { key: "choices", label: "Choices", type: "json", required: false, description: "Multiple-mode ordered choices: {id, label, expression}; exactly one choice must set isDefault=true and needs no expression." },
        ],
        outgoing: "branch",
    },
    {
        type: "action",
        label: "Action",
        description: "A side-effect: call a tool, post a message, hit a webhook.",
        configFields: [
            { key: "label", label: "Label", type: "string", required: false, description: "Display name." },
            { key: "actionType", label: "Action type", type: "string", required: true, description: `One of: ${ACTION_TYPES.join(", ")}.` },
        ],
        outgoing: "single",
    },
];
/**
 * Assemble the full machine-readable catalogue. Served to agents (the
 * authoring skill reads this so it knows exactly what to emit) and to the
 * UI. Stable shape — additive changes only.
 */
export function buildWorkflowCapabilityCatalogue() {
    return {
        nodeTypes: WORKFLOW_NODE_CATALOGUE,
        triggers: WORKFLOW_TRIGGER_CATALOGUE,
        actions: WORKFLOW_ACTION_CATALOGUE,
        conditionOperators: WORKFLOW_CONDITION_OPERATORS,
        edge: {
            description: "Edges connect node outputs to node inputs. Binary conditions use sourceHandle 'yes' | 'no'; multiple-choice conditions use a choice id; all other nodes leave sourceHandle null.",
            fields: ["id", "source", "target", "sourceHandle", "label", "animated"],
        },
        loops: {
            description: `Loops are allowed when a condition inside the loop has a connected branch that exits it. Runs stop after ${MAX_WORKFLOW_STEP_EXECUTIONS} total step executions.`,
            maxStepExecutions: MAX_WORKFLOW_STEP_EXECUTIONS,
        },
    };
}
/**
 * Pure structural validation of a workflow graph. Catches the classes of
 * problem we can detect without the DB: missing/duplicate trigger, dangling
 * edges, unreachable nodes, missing required node config, unsafe loops, and
 * condition branches with no handler.
 *
 * Semantic checks that need the DB (agent exists in tenant, condition
 * expression parses with the real evaluator) live in
 * server/services/workflow-validate.ts and are layered on top of this.
 *
 * Returns [] when the graph is runnable. `errors` block execution;
 * `warnings` are advisory.
 */
export function validateWorkflowGraph(graph) {
    const issues = [];
    const nodes = graph.nodes ?? [];
    const edges = graph.edges ?? [];
    if (nodes.length === 0) {
        issues.push({
            level: "error",
            code: "empty_graph",
            message: "Workflow has no nodes. Add a trigger and at least one step.",
        });
        return issues;
    }
    // Duplicate node ids.
    const seen = new Set();
    for (const n of nodes) {
        if (seen.has(n.id)) {
            issues.push({
                level: "error",
                code: "duplicate_node_id",
                message: `Duplicate node id "${n.id}".`,
                nodeId: n.id,
            });
        }
        seen.add(n.id);
    }
    const nodeById = new Map(nodes.map((n) => [n.id, n]));
    // Trigger cardinality.
    const triggers = nodes.filter((n) => n.type === "trigger");
    if (triggers.length === 0) {
        issues.push({
            level: "error",
            code: "no_trigger",
            message: "Workflow has no trigger node. Every workflow needs exactly one.",
        });
    }
    else if (triggers.length > 1) {
        issues.push({
            level: "warning",
            code: "multiple_triggers",
            message: `Workflow has ${triggers.length} trigger nodes; only the first is used.`,
            nodeId: triggers[1].id,
        });
    }
    // Edge endpoints must reference real nodes.
    for (const e of edges) {
        if (!nodeById.has(e.source)) {
            issues.push({
                level: "error",
                code: "dangling_edge_source",
                message: `Edge "${e.id}" starts from a node that doesn't exist ("${e.source}").`,
                edgeId: e.id,
            });
        }
        if (!nodeById.has(e.target)) {
            issues.push({
                level: "error",
                code: "dangling_edge_target",
                message: `Edge "${e.id}" points to a node that doesn't exist ("${e.target}").`,
                edgeId: e.id,
            });
        }
    }
    // Per-node config requirements.
    for (const n of nodes) {
        const data = (n.data ?? {});
        if (n.type === "trigger") {
            const subtype = data.subtype ?? "manual";
            if (subtype === "schedule" && !str(data.cronExpression)) {
                issues.push({
                    level: "error",
                    code: "schedule_missing_cron",
                    message: `Schedule trigger "${label(n)}" has no cron expression.`,
                    nodeId: n.id,
                });
            }
            if (subtype === "channel_message" && !str(data.channelId)) {
                issues.push({
                    level: "error",
                    code: "channel_trigger_missing_channel",
                    message: `Channel-message trigger "${label(n)}" has no channel selected.`,
                    nodeId: n.id,
                });
            }
            const outgoing = edges.filter((e) => e.source === n.id);
            if (triggers[0]?.id === n.id && outgoing.length === 0) {
                issues.push({
                    level: "error",
                    code: "trigger_no_outgoing",
                    message: "The trigger has no outgoing connection — there's nothing to run.",
                    nodeId: n.id,
                });
            }
        }
        if (n.type === "agent" && !str(data.agentId)) {
            issues.push({
                level: "error",
                code: "agent_node_missing_agent",
                message: `Agent step "${label(n)}" has no agent selected.`,
                nodeId: n.id,
            });
        }
        if (n.type === "action") {
            const actionType = data.actionType ?? "";
            const config = (data.config ?? {});
            const spec = WORKFLOW_ACTION_CATALOGUE.find((s) => s.actionType === actionType);
            if (!spec) {
                issues.push({
                    level: "error",
                    code: "unknown_action_type",
                    message: `Action "${label(n)}" has an unknown action type "${actionType}".`,
                    nodeId: n.id,
                });
            }
            else {
                for (const f of spec.configFields) {
                    if (f.required && config[f.key] == null) {
                        issues.push({
                            level: "error",
                            code: "action_missing_config",
                            message: `Action "${label(n)}" (${spec.label}) is missing required field "${f.key}".`,
                            nodeId: n.id,
                        });
                    }
                }
                if (actionType === "wait_for_email_reply" &&
                    !str(config.messageId) &&
                    !str(config.conversationId) &&
                    !str(config.internetMessageId)) {
                    issues.push({
                        level: "error",
                        code: "email_wait_missing_identity",
                        message: `Action "${label(n)}" needs a message id, conversation id, or internet message id.`,
                        nodeId: n.id,
                    });
                }
                if (actionType === "wait_for_email_reply" && !str(config.deadlineAt)) {
                    const waitDuration = Number(config.waitDuration);
                    const waitUnit = str(config.waitUnit);
                    if (!Number.isInteger(waitDuration) ||
                        waitDuration <= 0 ||
                        !["minutes", "hours", "days"].includes(waitUnit)) {
                        issues.push({
                            level: "error",
                            code: "email_wait_duration_invalid",
                            message: `Action "${label(n)}" needs a positive wait duration and a unit of minutes, hours, or days.`,
                            nodeId: n.id,
                        });
                    }
                }
            }
        }
        if (n.type === "condition") {
            const outgoing = edges.filter((e) => e.source === n.id);
            const data = n.data;
            if (data.mode === "multiple") {
                const choices = data.choices ?? [];
                const choiceIds = new Set(choices.map((choice) => choice.id));
                const defaults = choices.filter((choice) => choice.isDefault);
                if (choices.length < 2) {
                    issues.push({
                        level: "error",
                        code: "condition_choices_missing",
                        message: `Condition "${label(n)}" needs at least two choices.`,
                        nodeId: n.id,
                    });
                }
                if (choiceIds.size !== choices.length) {
                    issues.push({
                        level: "error",
                        code: "condition_choice_ids_duplicate",
                        message: `Condition "${label(n)}" has duplicate choice IDs.`,
                        nodeId: n.id,
                    });
                }
                if (defaults.length !== 1) {
                    issues.push({
                        level: "error",
                        code: "condition_default_choice_invalid",
                        message: `Condition "${label(n)}" needs exactly one Otherwise choice.`,
                        nodeId: n.id,
                    });
                }
                for (const choice of choices) {
                    if (!outgoing.some((edge) => edge.sourceHandle === choice.id)) {
                        issues.push({
                            level: "warning",
                            code: "condition_choice_unconnected",
                            message: `Choice "${choice.label}" in condition "${label(n)}" is not connected.`,
                            nodeId: n.id,
                        });
                    }
                }
                for (const edge of outgoing) {
                    if (edge.sourceHandle && !choiceIds.has(edge.sourceHandle)) {
                        issues.push({
                            level: "error",
                            code: "condition_choice_edge_unknown",
                            message: `Condition "${label(n)}" has an edge for an unknown choice.`,
                            nodeId: n.id,
                        });
                    }
                }
                continue;
            }
            const hasYes = outgoing.some((e) => e.sourceHandle === "yes");
            const hasNo = outgoing.some((e) => e.sourceHandle === "no");
            if (!hasYes && !hasNo) {
                issues.push({
                    level: "warning",
                    code: "condition_no_branches",
                    message: `Condition "${label(n)}" has no yes/no branches — the run will end here.`,
                    nodeId: n.id,
                });
            }
            else if (!hasNo) {
                issues.push({
                    level: "warning",
                    code: "condition_no_no_branch",
                    message: `Condition "${label(n)}" has no "no" branch — a false result ends the run.`,
                    nodeId: n.id,
                });
            }
        }
    }
    // Reachability + safe-loop validation from the (first) trigger.
    if (triggers[0]) {
        const start = triggers[0].id;
        const adjacency = new Map();
        for (const e of edges) {
            if (!adjacency.has(e.source))
                adjacency.set(e.source, []);
            adjacency.get(e.source).push(e.target);
        }
        // Reachable set (BFS).
        const reachable = new Set([start]);
        const queue = [start];
        while (queue.length) {
            const cur = queue.shift();
            for (const next of adjacency.get(cur) ?? []) {
                if (!reachable.has(next)) {
                    reachable.add(next);
                    queue.push(next);
                }
            }
        }
        for (const n of nodes) {
            if (n.type === "trigger")
                continue;
            if (!reachable.has(n.id)) {
                issues.push({
                    level: "warning",
                    code: "unreachable_node",
                    message: `Node "${label(n)}" can't be reached from the trigger and will never run.`,
                    nodeId: n.id,
                });
            }
        }
        // Find strongly connected components. A cyclic component is safe only
        // when a condition inside it controls a branch that leaves it.
        let nextIndex = 0;
        const indexes = new Map();
        const lowLinks = new Map();
        const componentStack = [];
        const onStack = new Set();
        const components = [];
        const visit = (nodeId) => {
            indexes.set(nodeId, nextIndex);
            lowLinks.set(nodeId, nextIndex);
            nextIndex += 1;
            componentStack.push(nodeId);
            onStack.add(nodeId);
            for (const targetId of adjacency.get(nodeId) ?? []) {
                if (!nodeById.has(targetId))
                    continue;
                if (!indexes.has(targetId)) {
                    visit(targetId);
                    lowLinks.set(nodeId, Math.min(lowLinks.get(nodeId), lowLinks.get(targetId)));
                }
                else if (onStack.has(targetId)) {
                    lowLinks.set(nodeId, Math.min(lowLinks.get(nodeId), indexes.get(targetId)));
                }
            }
            if (lowLinks.get(nodeId) !== indexes.get(nodeId))
                return;
            const component = [];
            let memberId;
            do {
                memberId = componentStack.pop();
                if (!memberId)
                    break;
                onStack.delete(memberId);
                component.push(memberId);
            } while (memberId !== nodeId);
            components.push(component);
        };
        for (const n of nodes) {
            if (!indexes.has(n.id))
                visit(n.id);
        }
        for (const component of components) {
            const members = new Set(component);
            const isCycle = component.length > 1 ||
                edges.some((edge) => edge.source === component[0] && edge.target === component[0]);
            if (!isCycle)
                continue;
            const controlledExit = edges.some((edge) => {
                if (!members.has(edge.source) || members.has(edge.target))
                    return false;
                const sourceNode = nodeById.get(edge.source);
                return sourceNode?.type === "condition" && Boolean(edge.sourceHandle);
            });
            const loopLabel = component
                .map((id) => label(nodeById.get(id)))
                .join(", ");
            const conditionId = component.find((id) => nodeById.get(id)?.type === "condition");
            if (!controlledExit) {
                issues.push({
                    level: "error",
                    code: "loop_without_exit",
                    message: `Loop "${loopLabel}" needs a condition branch that exits the loop.`,
                    nodeId: conditionId ?? component[0],
                });
            }
            else {
                issues.push({
                    level: "warning",
                    code: "loop_detected",
                    message: `Loop "${loopLabel}" is allowed because it has a condition-controlled exit. Runs stop automatically after ${MAX_WORKFLOW_STEP_EXECUTIONS} step executions.`,
                    nodeId: conditionId ?? component[0],
                });
            }
        }
    }
    return issues;
}
/** Convenience: true when the graph has no blocking errors. */
export function isWorkflowGraphRunnable(graph) {
    return !validateWorkflowGraph(graph).some((i) => i.level === "error");
}
function str(v) {
    return typeof v === "string" ? v.trim() : "";
}
function label(n) {
    const data = (n.data ?? {});
    return data.label && data.label.trim() ? data.label : n.id;
}
//# sourceMappingURL=workflow-capabilities.js.map